import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ProgressBar from '../components/ProgressBar';
import QuestionCard from '../components/QuestionCard';
import { shortTestData, fullTestData } from '../data/testData';
import { calculateResults } from '../utils/calculator';
import './Test.css';

const Test = () => {
  const { type } = useParams();
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [userAnswers, setUserAnswers] = useState({});
  const [selectedOption, setSelectedOption] = useState(null);
  const [testData, setTestData] = useState(null);
  const autoAdvanceTimer = useRef(null);

  useEffect(() => {
    // 根据测试类型加载数据
    if (type === 'short') {
      setTestData(shortTestData);
    } else if (type === 'full') {
      setTestData(fullTestData);
    } else {
      // 默认使用快速测试
      setTestData(shortTestData);
    }
  }, [type]);

  // 定时器卸载清理：确保在组件卸载时清除自动跳转定时器
  useEffect(() => {
    return () => {
      if (autoAdvanceTimer.current) {
        clearTimeout(autoAdvanceTimer.current);
        autoAdvanceTimer.current = null;
      }
    };
  }, []);

  // 同步 selectedOption 与当前题及已保存答案，放在顶层 hooks 保证 hook 顺序稳定
  useEffect(() => {
    setSelectedOption(userAnswers[currentQuestion] || null);
  }, [currentQuestion, userAnswers]);

  const handleOptionSelect = (value) => {
    // clear any pending auto-advance
    if (autoAdvanceTimer.current) {
      clearTimeout(autoAdvanceTimer.current);
      autoAdvanceTimer.current = null;
    }

    setSelectedOption(value);
    const newAnswers = { ...userAnswers, [currentQuestion]: value };
    setUserAnswers(newAnswers);

    // auto-advance after short delay so user sees selection
    autoAdvanceTimer.current = setTimeout(() => {
      const total = testData?.questions.length || 0;
      if (currentQuestion < total) {
        setCurrentQuestion((prev) => prev + 1);
      } else {
        completeTest();
      }
      autoAdvanceTimer.current = null;
    }, 350);
  };

  const handleNextQuestion = () => {
    if (!selectedOption) return;

    const totalQuestions = testData?.questions.length || 0;

    if (currentQuestion < totalQuestions) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedOption(userAnswers[currentQuestion + 1] || null);
    } else {
      // 完成测试，计算结果
      completeTest();
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestion > 1) {
      setCurrentQuestion(currentQuestion - 1);
      setSelectedOption(userAnswers[currentQuestion - 1] || null);
    }
  };

  const completeTest = () => {
    if (!testData) return;

    const results = calculateResults(userAnswers, testData);

    // 保存结果到localStorage
    localStorage.setItem(`attachment_test_${type}_results`, JSON.stringify(results));
    localStorage.setItem(`attachment_test_${type}_answers`, JSON.stringify(userAnswers));

    // 跳转到结果页
    navigate(`/result/${type}`);
  };

  if (!testData) {
    return <div className="loading">加载中...</div>;
  }

  const currentQuestionData = testData.questions[currentQuestion - 1];
  const totalQuestions = testData.questions.length;
  const isFirst = currentQuestion === 1;
  const isLast = currentQuestion === totalQuestions;
  const defaultOptionLabels = ['非常不同意','不同意','稍微不同意','中立','稍微同意','同意','非常同意'];

  // no-op: all cleanup/sync effects are declared above

  return (
    <div className="test-container">
      <div className="page-container">
        <div className="page-header">
          <h1 className="page-title">
            {type === 'short' ? '快速测试' : '深度测试'}
          </h1>
          <p className="page-subtitle">
            请根据你的实际情况选择最符合的选项
          </p>
          <ProgressBar current={currentQuestion} total={totalQuestions} />
        </div>

        <div className="page-content">
          <div className="question-section">
            <div className="question-text">
              {currentQuestionData?.text}
            </div>

            <div className="options-container" role="list">
              {[1, 2, 3, 4, 5, 6, 7].map((option) => {
                const optionText = currentQuestionData?.options?.[option - 1] || defaultOptionLabels[option - 1] || '';
                return (
                  <div
                    key={option}
                    role="listitem"
                    tabIndex={0}
                    className={`option-item ${selectedOption === option ? 'selected' : ''}`}
                    onClick={() => handleOptionSelect(option)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleOptionSelect(option); }}
                  >
                    <input
                      type="radio"
                      name="option"
                      value={option}
                      checked={selectedOption === option}
                      onChange={() => handleOptionSelect(option)}
                      className="option-radio"
                      aria-hidden
                    />
                    <div className="option-content">
                      <div className="option-label" aria-hidden>{option}</div>
                      <div className="option-text">
                        {optionText}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="navigation-section">
            <div className="nav-buttons">
              <button
                className="nav-btn prev"
                onClick={handlePreviousQuestion}
                disabled={isFirst}
              >
                上一题
              </button>
              <button
                className="nav-btn next"
                onClick={handleNextQuestion}
                disabled={!selectedOption}
              >
                {isLast ? '完成测试' : '下一题'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Test;
